"use client"

import { useRef, useState, useEffect } from "react"
import { Box, Grid, Typography, Container, useMediaQuery, useTheme } from "@mui/material"
import { motion, AnimatePresence } from "framer-motion"

const features = [
  {
    title: "Auto Scalability",
    description: "Adjust your setup on the fly to keep up with changing needs making sure everything runs.",
  },
  {
    title: "Robust Security",
    description: "Implement industry-standard practices to secure your microservices and data.",
  },
  {
    title: "Disaster Recovery",
    description: "Lower risks by using Velero's quick and easy backup and recovery tools.",
  },
  {
    title: "Real-Time Observability",
    description: "Works with Prometheus and Grafana to provide actionable insights across your ecosystem.",
  },
  {
    title: "Streamlined Automation",
    description: "Mitigate risks with Velero's efficient backup and recovery tools.",
  },
  {
    title: "Cost Efficiency",
    description: "Optimize infrastructure for maximum performance with minimum cost.",
  },
  {
    title: "High Availability",
    description: "Keep things running well with reliable ways to scale up and recovery options.",
  },
]

// Feature Card component with animations
const FeatureCard = ({ feature, index, rowIndex, isMobile, isActive = false }) => {
  const [isVisible, setIsVisible] = useState(false)
  const cardRef = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.unobserve(entry.target)
        }
      },
      { threshold: 0.2 }, // Trigger when 20% of the card is visible
    )

    if (cardRef.current) {
      observer.observe(cardRef.current)
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current)
      }
    }
  }, [])

  // Calculate animation delay based on position
  const getAnimationDelay = () => {
    // Different delay for each card in the row
    const baseDelay = 0.1
    const rowDelay = rowIndex * 0.1 // Additional delay for each row
    const columnDelay = index * 0.15 // Delay within the row

    return baseDelay + rowDelay + columnDelay
  }

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 50 }}
      animate={{
        opacity: isVisible ? 1 : 0,
        y: isVisible ? 0 : 50,
        scale: isVisible ? 1 : 0.9,
      }}
      transition={{
        duration: 0.6,
        delay: getAnimationDelay(),
        ease: "easeOut",
      }}
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: isMobile ? "150px" : "290px",
          width: isMobile ? "260px" : "370px",
          backgroundColor: "#fad32e",
          borderRadius: "16px",
          p: isMobile ? 2.5 : 3,
          overflow: "hidden",
          //boxShadow: isActive ? "0 10px 40px rgba(250, 211, 46, 0.4)" : "0 10px 30px rgba(0, 0, 0, 0.1)",
          transition: "all 0.3s ease",
          transform: isActive ? "scale(1.05)" : "scale(1)",
          "&:hover": {
            transform: isActive ? "scale(1.05) translateY(-5px)" : "translateY(-10px)",
            boxShadow: "0 15px 35px rgba(250, 211, 46, 0.3)",
          },

          // Dot pattern background
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: "radial-gradient(#2f2f2f 1px, transparent 1px)",
            backgroundSize: "10px 10px",
            opacity: 0.2,
            zIndex: 0,
          },
        }}
      >
        <Box sx={{ position: "relative", zIndex: 1, height: "100%", display: "flex", flexDirection: "column" }}>
          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontWeight: "bold",
              mb: 1,
              color: "#000000",
              fontSize: isMobile ? "1.1rem" : "1.25rem",
            }}
          >
            {feature.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#000000",
              fontSize: isMobile ? "0.8rem" : "0.9rem",
              flexGrow: 1,
            }}
          >
            {feature.description}
          </Typography>
        </Box>
      </Box>
    </motion.div>
  )
}

const CoreFeatures = () => {
  const [titleVisible, setTitleVisible] = useState(false)
  const titleRef = useRef(null)
  const carouselRef = useRef(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const [currentIndex, setCurrentIndex] = useState(0)
  const autoPlayIntervalRef = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTitleVisible(true)
          observer.unobserve(entry.target)
        }
      },
      { threshold: 0.1 },
    )

    if (titleRef.current) {
      observer.observe(titleRef.current)
    }

    return () => {
      if (titleRef.current) {
        observer.unobserve(titleRef.current)
      }
    }
  }, [])

  // Auto-carousel effect
  useEffect(() => {
    if (isMobile) {
      autoPlayIntervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          const nextIndex = prevIndex + 1
          return nextIndex >= features.length ? 0 : nextIndex
        })
      }, 4000) // Change slide every 4 seconds
    }

    return () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current)
      }
    }
  }, [isMobile])

  // Handle touch events for mobile swipe
  const handleTouchStart = (e) => {
    // Store the initial touch position if needed for swipe implementation
  }

  const handleTouchEnd = (e) => {
    // Handle swipe if needed
  }

  return (
    <Box
      sx={{
        py: 8,
        backgroundColor: "#000000",
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          ref={titleRef}
          initial={{ opacity: 0, y: -30 }}
          animate={{
            opacity: titleVisible ? 1 : 0,
            y: titleVisible ? 0 : -30,
          }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h2"
            component="h2"
            align="center"
            sx={{
              fontWeight: "semi-bold",
              mb: 6,
              color: "#fad32e",
              fontFamily: "Poppins, sans-serif",
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              textTransform: "uppercase",
            }}
          >
            Core Features
          </Typography>
        </motion.div>

        {/* Mobile Carousel */}
        {isMobile && (
          <Box sx={{ position: "relative", mb: 4 }}>
            {/* Carousel Container */}
            <Box
              ref={carouselRef}
              sx={{
                width: "100%",
                position: "relative",
                overflow: "hidden",
                height: "260px", // Increased height to accommodate the card with scaling
                my: 4,
                pt: 3,
                display: "flex",
                alignItems: "center", // Center the card vertically
                justifyContent: "center", // Center the card horizontally
              }}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <AnimatePresence initial={false} custom={currentIndex}>
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    custom={index}
                    initial={{
                      opacity: 0,
                      x: index > currentIndex ? 300 : -300,
                    }}
                    animate={{
                      opacity: index === currentIndex ? 1 : 0,
                      x: index === currentIndex ? 0 : index > currentIndex ? 300 : -300,
                      scale: index === currentIndex ? 1 : 0.8,
                    }}
                    exit={{
                      opacity: 0,
                      x: index < currentIndex ? -300 : 300,
                      scale: 0.8,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 30,
                    }}
                    style={{
                      position: "absolute",
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {index === currentIndex && (
                      <FeatureCard feature={feature} index={index} rowIndex={0} isMobile={isMobile} isActive={true} />
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </Box>
          </Box>
        )}

        {/* Desktop Grid */}
        {!isMobile && (
          <Grid container spacing={3} justifyContent="center" alignItems="center">
            {/* First row - 3 cards */}
            {features.slice(0, 3).map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <FeatureCard feature={feature} index={index} rowIndex={0} isMobile={isMobile} />
              </Grid>
            ))}

            {/* Second row - 3 cards */}
            {features.slice(3, 6).map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index + 3}>
                <FeatureCard feature={feature} index={index} rowIndex={1} isMobile={isMobile} />
              </Grid>
            ))}

            {/* Third row - 1 centered card */}
            <Grid item xs={12} container justifyContent="center" alignItems="center">
              <FeatureCard feature={features[6]} index={0} rowIndex={2} isMobile={isMobile} />
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  )
}

export default CoreFeatures




// import React, { useRef, useState, useEffect } from 'react';
// import { Box, Grid, Typography, Container, IconButton, useMediaQuery, useTheme } from '@mui/material';
// import { motion, AnimatePresence } from 'framer-motion';
// import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
// import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

// const features = [
//   {
//     title: "Auto Scalability",
//     description: "Adjust your setup on the fly to keep up with changing needs making sure everything runs."
//   },
//   {
//     title: "Robust Security",
//     description: "Implement industry-standard practices to secure your microservices and data."
//   },
//   {
//     title: "Disaster Recovery",
//     description: "Lower risks by using Velero's quick and easy backup and recovery tools."
//   },
//   {
//     title: "Real-Time Observability",
//     description: "Works with Prometheus and Grafana to provide actionable insights across your ecosystem."
//   },
//   {
//     title: "Streamlined Automation",
//     description: "Mitigate risks with Velero's efficient backup and recovery tools."
//   },
//   {
//     title: "Cost Efficiency",
//     description: "Optimize infrastructure for maximum performance with minimum cost."
//   },
//   {
//     title: "High Availability",
//     description: "Keep things running well with reliable ways to scale up and recovery options."
//   }
// ];

// // Feature Card component with animations
// const FeatureCard = ({ feature, index, rowIndex, isMobile, isActive = false }) => {
//   const [isVisible, setIsVisible] = useState(false);
//   const cardRef = useRef(null);

//   useEffect(() => {
//     const observer = new IntersectionObserver(
//       ([entry]) => {
//         if (entry.isIntersecting) {
//           setIsVisible(true);
//           observer.unobserve(entry.target);
//         }
//       },
//       { threshold: 0.2 } // Trigger when 20% of the card is visible
//     );

//     if (cardRef.current) {
//       observer.observe(cardRef.current);
//     }

//     return () => {
//       if (cardRef.current) {
//         observer.unobserve(cardRef.current);
//       }
//     };
//   }, []);

//   // Calculate animation delay based on position
//   const getAnimationDelay = () => {
//     // Different delay for each card in the row
//     const baseDelay = 0.1;
//     const rowDelay = rowIndex * 0.1; // Additional delay for each row
//     const columnDelay = index * 0.15; // Delay within the row
    
//     return baseDelay + rowDelay + columnDelay;
//   };

//   return (
//     <motion.div
//       ref={cardRef}
//       initial={{ opacity: 0, y: 50 }}
//       animate={{ 
//         opacity: isVisible ? 1 : 0, 
//         y: isVisible ? 0 : 50,
//         scale: isVisible ? 1 : 0.9
//       }}
//       transition={{ 
//         duration: 0.6, 
//         delay: getAnimationDelay(),
//         ease: "easeOut"
//       }}
//       style={{ 
//         height: '100%', 
//         width: '100%',
//         display: 'flex',
//         justifyContent: 'center'
//       }}
//     >
//       <Box
//         sx={{
//           position: 'relative',
//           height: isMobile ? '200px' : '250px',
//           width: isMobile ? '260px' : '320px',
//           backgroundColor: '#fad32e',
//           borderRadius: '16px',
//           p: isMobile ? 2.5 : 3,
//           overflow: 'hidden',
//           boxShadow: isActive 
//             ? '0 10px 40px rgba(250, 211, 46, 0.4)' 
//             : '0 10px 30px rgba(0, 0, 0, 0.1)',
//           transition: 'all 0.3s ease',
//           transform: isActive ? 'scale(1.05)' : 'scale(1)',
//           '&:hover': {
//             transform: isActive ? 'scale(1.05) translateY(-5px)' : 'translateY(-10px)',
//             boxShadow: '0 15px 35px rgba(250, 211, 46, 0.3)',
//           },
          
//           // Dot pattern background
//           '&::before': {
//             content: '""',
//             position: 'absolute',
//             top: 0,
//             left: 0,
//             right: 0,
//             bottom: 0,
//             backgroundImage: 'radial-gradient(#2f2f2f 1px, transparent 1px)',
//             backgroundSize: '10px 10px',
//             opacity: 0.2,
//             zIndex: 0,
//           }
//         }}
//       >
//         <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
//           <Typography
//             variant="h6"
//             component="h3"
//             sx={{
//               fontWeight: 'bold',
//               mb: 1,
//               color: '#000000',
//               fontSize: isMobile ? '1.1rem' : '1.25rem',
//             }}
//           >
//             {feature.title}
//           </Typography>
//           <Typography
//             variant="body2"
//             sx={{
//               color: '#000000',
//               fontSize: isMobile ? '0.8rem' : '0.9rem',
//               flexGrow: 1,
//             }}
//           >
//             {feature.description}
//           </Typography>
//         </Box>
//       </Box>
//     </motion.div>
//   );
// };

// const CoreFeatures = () => {
//   const [titleVisible, setTitleVisible] = useState(false);
//   const titleRef = useRef(null);
//   const carouselRef = useRef(null);
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
//   const [currentIndex, setCurrentIndex] = useState(0);

//   useEffect(() => {
//     const observer = new IntersectionObserver(
//       ([entry]) => {
//         if (entry.isIntersecting) {
//           setTitleVisible(true);
//           observer.unobserve(entry.target);
//         }
//       },
//       { threshold: 0.1 }
//     );

//     if (titleRef.current) {
//       observer.observe(titleRef.current);
//     }

//     return () => {
//       if (titleRef.current) {
//         observer.unobserve(titleRef.current);
//       }
//     };
//   }, []);

//   const handlePrev = () => {
//     setCurrentIndex((prev) => (prev > 0 ? prev - 1 : 0));
//   };

//   const handleNext = () => {
//     setCurrentIndex((prev) => (prev < features.length - 1 ? prev + 1 : prev));
//   };

//   return (
//     <Box sx={{ 
//       py: 8, 
//       backgroundColor: '#000000',
//     }}>
//       <Container maxWidth="lg">
//         <motion.div
//           ref={titleRef}
//           initial={{ opacity: 0, y: -30 }}
//           animate={{ 
//             opacity: titleVisible ? 1 : 0, 
//             y: titleVisible ? 0 : -30 
//           }}
//           transition={{ duration: 0.8 }}
//         >
//           <Typography
//             variant="h2"
//             component="h2"
//             align="center"
//             sx={{
//               fontWeight: 'semi-bold',
//               mb: 6,
//               color: '#fad32e',
//               fontFamily: 'Poppins, sans-serif',
//               fontSize: { xs: '2.5rem', md: '3.5rem' },
//               textTransform: 'uppercase',
//             }}
//           >
//             Core Features
//           </Typography>
//         </motion.div>

//         {/* Mobile Carousel */}
//         {isMobile && (
//           <Box sx={{ position: 'relative', mb: 4 }}>
//             {/* Carousel Navigation Arrows - Positioned on sides */}
//             <Box sx={{ 
//               position: 'absolute', 
//               top: '50%', 
//               left: -20, 
//               transform: 'translateY(-50%)',
//               zIndex: 10
//             }}>
//               <IconButton
//                 onClick={handlePrev}
//                 disabled={currentIndex === 0}
//                 sx={{
//                   color: '#fad32e',
//                   bgcolor: 'rgba(0, 0, 0, 0.7)',
//                   '&:disabled': {
//                     color: 'rgba(250, 211, 46, 0.3)',
//                     bgcolor: 'rgba(0, 0, 0, 0.3)',
//                   },
//                   '&:hover': {
//                     bgcolor: 'rgba(0, 0, 0, 0.9)',
//                   },
//                   boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
//                 }}
//               >
//                 <KeyboardArrowLeftIcon />
//               </IconButton>
//             </Box>
            
//             <Box sx={{ 
//               position: 'absolute', 
//               top: '50%', 
//               right: -20, 
//               transform: 'translateY(-50%)',
//               zIndex: 10
//             }}>
//               <IconButton
//                 onClick={handleNext}
//                 disabled={currentIndex === features.length - 1}
//                 sx={{
//                   color: '#fad32e',
//                   bgcolor: 'rgba(0, 0, 0, 0.7)',
//                   '&:disabled': {
//                     color: 'rgba(250, 211, 46, 0.3)',
//                     bgcolor: 'rgba(0, 0, 0, 0.3)',
//                   },
//                   '&:hover': {
//                     bgcolor: 'rgba(0, 0, 0, 0.9)',
//                   },
//                   boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
//                 }}
//               >
//                 <KeyboardArrowRightIcon />
//               </IconButton>
//             </Box>
            
//             {/* Carousel Container */}
//             <Box
//               ref={carouselRef}
//               sx={{
//                 width: '100%',
//                 position: 'relative',
//                 overflow: 'hidden',
//                 height: '220px', // Fixed height for the carousel
//                 my: 4,
//               }}
//             >
//               <AnimatePresence initial={false} custom={currentIndex}>
//                 {features.map((feature, index) => (
//                   <motion.div
//                     key={index}
//                     custom={index}
//                     initial={{ 
//                       opacity: 0,
//                       x: index > currentIndex ? 300 : -300
//                     }}
//                     animate={{ 
//                       opacity: index === currentIndex ? 1 : 0,
//                       x: index === currentIndex ? 0 : (index > currentIndex ? 300 : -300),
//                       scale: index === currentIndex ? 1 : 0.8,
//                     }}
//                     exit={{ 
//                       opacity: 0,
//                       x: index < currentIndex ? -300 : 300,
//                       scale: 0.8,
//                     }}
//                     transition={{
//                       type: 'spring',
//                       stiffness: 300,
//                       damping: 30,
//                     }}
//                     style={{
//                       position: 'absolute',
//                       width: '100%',
//                       height: '100%',
//                       display: 'flex',
//                       justifyContent: 'center',
//                       alignItems: 'center',
//                     }}
//                   >
//                     {index === currentIndex && (
//                       <FeatureCard 
//                         feature={feature} 
//                         index={index} 
//                         rowIndex={0} 
//                         isMobile={isMobile}
//                         isActive={true}
//                       />
//                     )}
//                   </motion.div>
//                 ))}
//               </AnimatePresence>
//             </Box>
            
//             {/* Progress Bar */}
//             <Box sx={{ 
//               width: '100%', 
//               height: '4px', 
//               bgcolor: 'rgba(250, 211, 46, 0.2)',
//               borderRadius: '2px',
//               mt: 2,
//               overflow: 'hidden'
//             }}>
//               <Box sx={{ 
//                 width: `${(currentIndex + 1) / features.length * 100}%`, 
//                 height: '100%', 
//                 bgcolor: '#fad32e',
//                 transition: 'width 0.3s ease'
//               }} />
//             </Box>
            
//             {/* Navigation Dots */}
//             <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
//               {features.map((_, index) => (
//                 <Box
//                   key={index}
//                   onClick={() => setCurrentIndex(index)}
//                   sx={{
//                     width: 8,
//                     height: 8,
//                     borderRadius: '50%',
//                     mx: 0.5,
//                     bgcolor: currentIndex === index ? '#fad32e' : 'rgba(250, 211, 46, 0.3)',
//                     cursor: 'pointer',
//                     transition: 'all 0.2s ease',
//                     '&:hover': {
//                       transform: 'scale(1.2)',
//                       bgcolor: currentIndex === index ? '#fad32e' : 'rgba(250, 211, 46, 0.5)',
//                     }
//                   }}
//                 />
//               ))}
//             </Box>
            
//             {/* Feature Title and Count */}
//             <Box sx={{ textAlign: 'center', mt: 2 }}>
//               <Typography
//                 variant="subtitle1"
//                 sx={{
//                   color: '#fad32e',
//                   fontWeight: 'bold',
//                   fontSize: '1rem',
//                 }}
//               >
//                 {features[currentIndex].title}
//               </Typography>
//               <Typography
//                 variant="caption"
//                 sx={{
//                   color: 'rgba(255, 255, 255, 0.6)',
//                   fontSize: '0.75rem',
//                 }}
//               >
//                 {currentIndex + 1} of {features.length}
//               </Typography>
//             </Box>
//           </Box>
//         )}

//         {/* Desktop Grid */}
//         {!isMobile && (
//           <Grid container spacing={3} justifyContent="center" alignItems="center">
//             {/* First row - 3 cards */}
//             {features.slice(0, 3).map((feature, index) => (
//               <Grid item xs={12} sm={6} md={4} key={index}>
//                 <FeatureCard feature={feature} index={index} rowIndex={0} isMobile={isMobile} />
//               </Grid>
//             ))}
            
//             {/* Second row - 3 cards */}
//             {features.slice(3, 6).map((feature, index) => (
//               <Grid item xs={12} sm={6} md={4} key={index + 3}>
//                 <FeatureCard feature={feature} index={index} rowIndex={1} isMobile={isMobile} />
//               </Grid>
//             ))}
            
//             {/* Third row - 1 centered card */}
//             <Grid item xs={12} container justifyContent="center" alignItems="center">
//               <FeatureCard feature={features[6]} index={0} rowIndex={2} isMobile={isMobile} />
//             </Grid>
//           </Grid>
//         )}
//       </Container>
//     </Box>
//   );
// };

// export default CoreFeatures;





// import React, { useRef, useState, useEffect } from 'react';
// import { Box, Grid, Typography, Container } from '@mui/material';
// import { motion } from 'framer-motion';

// const features = [
//   {
//     title: "Auto Scalability",
//     description: "Adjust your setup on the fly to keep up with changing needs making sure everything runs."
//   },
//   {
//     title: "Robust Security",
//     description: "Implement industry-standard practices to secure your microservices and data."
//   },
//   {
//     title: "Disaster Recovery",
//     description: "Lower risks by using Velero's quick and easy backup and recovery tools."
//   },
//   {
//     title: "Real-Time Observability",
//     description: "Works with Prometheus and Grafana to provide actionable insights across your ecosystem."
//   },
//   {
//     title: "Streamlined Automation",
//     description: "Mitigate risks with Velero's efficient backup and recovery tools."
//   },
//   {
//     title: "Cost Efficiency",
//     description: "Optimize infrastructure for maximum performance with minimum cost."
//   },
//   {
//     title: "High Availability",
//     description: "Keep things running well with reliable ways to scale up and recovery options."
//   }
// ];

// // Feature Card component with animations
// const FeatureCard = ({ feature, index, rowIndex }) => {
//   const [isVisible, setIsVisible] = useState(false);
//   const cardRef = useRef(null);

//   useEffect(() => {
//     const observer = new IntersectionObserver(
//       ([entry]) => {
//         if (entry.isIntersecting) {
//           setIsVisible(true);
//           observer.unobserve(entry.target);
//         }
//       },
//       { threshold: 0.2 } // Trigger when 20% of the card is visible
//     );

//     if (cardRef.current) {
//       observer.observe(cardRef.current);
//     }

//     return () => {
//       if (cardRef.current) {
//         observer.unobserve(cardRef.current);
//       }
//     };
//   }, []);

//   // Calculate animation delay based on position
//   const getAnimationDelay = () => {
//     // Different delay for each card in the row
//     const baseDelay = 0.1;
//     const rowDelay = rowIndex * 0.1; // Additional delay for each row
//     const columnDelay = index * 0.15; // Delay within the row
    
//     return baseDelay + rowDelay + columnDelay;
//   };

//   return (
//     <motion.div
//       ref={cardRef}
//       initial={{ opacity: 0, y: 50 }}
//       animate={{ 
//         opacity: isVisible ? 1 : 0, 
//         y: isVisible ? 0 : 50,
//         scale: isVisible ? 1 : 0.9
//       }}
//       transition={{ 
//         duration: 0.6, 
//         delay: getAnimationDelay(),
//         ease: "easeOut"
//       }}
//       style={{ height: '100%', width: '100%' }}
//     >
//       <Box
//         sx={{
//           position: 'relative',
//           height: {isMobile: '100px', sm: '250px', md: '250px'},
//           width: '320px',
//           backgroundColor: '#fad32e',
//           borderRadius: '16px',
//           p: 3,
//           overflow: 'hidden',
//           boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
//           transition: 'transform 0.3s ease, box-shadow 0.3s ease',
//           '&:hover': {
//             transform: 'translateY(-10px)',
//             boxShadow: '0 15px 35px rgba(250, 211, 46, 0.3)',
//           },
          
//           // Dot pattern background
//           '&::before': {
//             content: '""',
//             position: 'absolute',
//             top: 0,
//             left: 0,
//             right: 0,
//             bottom: 0,
//             backgroundImage: 'radial-gradient(#2f2f2f 1px, transparent 1px)',
//             backgroundSize: '10px 10px',
//             opacity: 0.2,
//             zIndex: 0,
//           }
//         }}
//       >
//         <Box sx={{ position: 'relative', zIndex: 1 }}>
//           <Typography
//             variant="h6"
//             component="h3"
//             sx={{
//               fontWeight: 'bold',
//               mb: 1,
//               color: '#000000',
//               mt: 22,
//             }}
//           >
//             {feature.title}
//           </Typography>
//           <Typography
//             variant="body2"
//             sx={{
//               color: '#000000',
//             }}
//           >
//             {feature.description}
//           </Typography>
//         </Box>
//       </Box>
//     </motion.div>
//   );
// };

// const CoreFeatures = () => {
//   const [titleVisible, setTitleVisible] = useState(false);
//   const titleRef = useRef(null);

//   useEffect(() => {
//     const observer = new IntersectionObserver(
//       ([entry]) => {
//         if (entry.isIntersecting) {
//           setTitleVisible(true);
//           observer.unobserve(entry.target);
//         }
//       },
//       { threshold: 0.1 }
//     );

//     if (titleRef.current) {
//       observer.observe(titleRef.current);
//     }

//     return () => {
//       if (titleRef.current) {
//         observer.unobserve(titleRef.current);
//       }
//     };
//   }, []);

//   return (
//     <Box sx={{ 
//       py: 8, 
//       backgroundColor: '#000000',
//     }}>
//       <Container maxWidth="lg">
//         <motion.div
//           ref={titleRef}
//           initial={{ opacity: 0, y: -30 }}
//           animate={{ 
//             opacity: titleVisible ? 1 : 0, 
//             y: titleVisible ? 0 : -30 
//           }}
//           transition={{ duration: 0.8 }}
//         >
//           <Typography
//             variant="h2"
//             component="h2"
//             align="center"
//             sx={{
//               fontWeight: 'semi-bold',
//               mb: 6,
//               color: '#fad32e',
//               fontFamily: 'Poppins, sans-serif',
//               fontSize: { xs: '2.5rem', md: '3.5rem' },
//               textTransform: 'uppercase',
//             }}
//           >
//             Core Features
//           </Typography>
//         </motion.div>

//         <Grid container spacing={3} justifyContent="center" alignItems="center">
//           {/* First row - 3 cards */}
//           {features.slice(0, 3).map((feature, index) => (
//             <Grid item xs={12} sm={6} md={4} key={index}>
//               <FeatureCard feature={feature} index={index} rowIndex={0} />
//             </Grid>
//           ))}
          
//           {/* Second row - 3 cards */}
//           {features.slice(3, 6).map((feature, index) => (
//             <Grid item xs={12} sm={6} md={4} key={index + 3}>
//               <FeatureCard feature={feature} index={index} rowIndex={1} />
//             </Grid>
//           ))}
          
//           {/* Third row - 1 centered card */}
//           <Grid item xs={12} container justifyContent="center" alignItems="center">
//             <FeatureCard feature={features[6]} index={0} rowIndex={2} />
//           </Grid>
//         </Grid>
//       </Container>
//     </Box>
//   );
// };

// export default CoreFeatures;
